import { AutoAwesome, CallEnd, Chat, CheckCircle, ExpandMore, Headset, HeadsetOff, NotificationImportant, OpenInFull, Person, Route } from '@mui/icons-material'
import { Box, Button, Card, Chip, IconButton, Typography } from '@mui/joy'
import { HorizontalDirectionIcon } from 'assets/icons/DatDirectionIcons'
import { getStatusColor, getStyles } from '../../../utils/styles'
import { useEffect, useRef, useState } from 'react'
import { CallStatus, VoiceTranscript, VoiceTranscriptRole } from '@numeo/types'
import { formatCurrency, formatLoadLocation, formatRatePerMile } from 'utils/formatters'
import { convertVoiceAgentToolCallToMessage } from 'utils/convert-agent-tool-call-to-message'
import { START_BOTTOM, WIDGET_HEIGHT, WIDGET_SPACING } from '../../../const'
import { CallDurationTimer } from '../../widget-subcomponents'
import { useAppSelector } from 'hooks'

interface MinimizedAICallWidgetProps {
    callId: string
    onMaximize: () => void
    index: number
    dispatcherRequestAlert: boolean
    rateHighlight: boolean
    isTransitioning: boolean
    takenOver: boolean
    loadingTakeOver: boolean
    isListening: boolean
    transcriptions: VoiceTranscript[]

    onTakeOver: () => void
    onEndCall: () => void
    toggleListening: () => void
    endingCall: boolean
}
export const MinimizedAICallWidget = ({
    callId,
    onMaximize,
    index,
    dispatcherRequestAlert,
    rateHighlight,
    isTransitioning,
    takenOver,
    loadingTakeOver,
    isListening,
    transcriptions,
    onTakeOver,
    onEndCall,
    toggleListening,
    endingCall,
}: MinimizedAICallWidgetProps) => {
    const [openTranscripts, setOpenTranscripts] = useState(false)
    const styles = getStyles({
        dispatcherRequestAlert,
        rateHighlight,
    })
    const transcriptsDivRef = useRef<HTMLDivElement>(null)
    const { activeAvailableCalls } = useAppSelector((state) => state.callCenter)
    const activeCallsCount = Object.values(activeAvailableCalls).length
    const activeAvailableCall = activeAvailableCalls[callId]
    const deadheadMiles = (activeAvailableCall.load.originDeadheadMiles?.miles || 0) + (activeAvailableCall.load.destinationDeadheadMiles?.miles || 0)

    const onToggleListeningMode = (event: React.MouseEvent) => {
        event.stopPropagation()
        toggleListening()
    }

    const onTakeOverButtonClick = (event: React.MouseEvent) => {
        event.stopPropagation()
        onTakeOver()
    }

    const onEndCallButtonClick = (event: React.MouseEvent) => {
        event.stopPropagation()
        onEndCall()
    }

    const onExpandTranscriptions = (event: React.MouseEvent) => {
        event.stopPropagation()
        setOpenTranscripts((prev) => !prev)
    }

    // Auto-scroll to bottom when new transcript arrives
    useEffect(() => {
        if (openTranscripts && transcriptions?.length) {
            transcriptsDivRef.current?.scrollIntoView({ behavior: 'smooth' })
        }
    }, [transcriptions, openTranscripts])

    return (
        <Box
            sx={{
                position: 'fixed',
                zIndex: 900 - index,
                right: WIDGET_SPACING,
                bottom: START_BOTTOM + index * (WIDGET_HEIGHT + WIDGET_SPACING),
                // Add stacking animation when multiple calls
                transition: 'all 0.3s ease-in-out',
                transform: activeCallsCount > 1 ? 'scale(0.95)' : 'scale(1)',
            }}
        >
            <Box
                onClick={onMaximize}
                sx={{
                    width: '600px',
                    minWidth: '480px',
                    maxWidth: '700px',
                    bgcolor: styles.backgroundColor,
                    borderRadius: 'md',
                    boxShadow: 'sm', // Reduced shadow
                    border: '2px solid',
                    borderColor: styles.borderColor,
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: 'md',
                        // Slightly intensify background on hover - only for rate and dispatcher
                        ...(dispatcherRequestAlert && { bgcolor: 'warning.100' }),
                        ...(!dispatcherRequestAlert && rateHighlight && { bgcolor: 'success.100' }),
                    },
                    ...(isTransitioning && { opacity: 0, transition: 'opacity 0.2s ease-out' }),
                    // Subtle pulse animation for urgent states
                    ...(dispatcherRequestAlert && {
                        animation: 'dispatcherPulse 2s ease-in-out infinite',
                        '@keyframes dispatcherPulse': {
                            '0%': {
                                bgcolor: 'warning.50',
                                borderColor: 'warning.300',
                            },
                            '50%': {
                                bgcolor: 'warning.100',
                                borderColor: 'warning.400',
                            },
                            '100%': {
                                bgcolor: 'warning.50',
                                borderColor: 'warning.300',
                            },
                        },
                    }),
                    // Gentle pulse for rate highlights (only if no dispatcher alert)
                    ...(!dispatcherRequestAlert &&
                        rateHighlight && {
                            animation: 'ratePulse 3s ease-in-out infinite',
                            '@keyframes ratePulse': {
                                '0%': {
                                    bgcolor: 'success.50',
                                    borderColor: 'success.300',
                                },
                                '50%': {
                                    bgcolor: 'success.100',
                                    borderColor: 'success.400',
                                },
                                '100%': {
                                    bgcolor: 'success.50',
                                    borderColor: 'success.300',
                                },
                            },
                        }),
                    // Initial entry animation
                    '@keyframes slideInFromBottom': {
                        from: {
                            transform: 'translateY(100%)',
                            opacity: 0,
                        },
                        to: {
                            transform: 'translateY(0)',
                            opacity: 1,
                        },
                    },
                    // Add entry animation only if no other animations are active
                    ...(!dispatcherRequestAlert &&
                        !rateHighlight && {
                            animation: 'slideInFromBottom 0.3s ease-out',
                        }),
                }}
            >
                {/* Header with call type and call status indicator */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: dispatcherRequestAlert ? 'warning.500' : getStatusColor(activeAvailableCall.call?.status),
                                animation: 'pulseDot 1.5s infinite',
                                '@keyframes pulseDot': {
                                    '0%': { opacity: 1 },
                                    '50%': { opacity: 0.4 },
                                    '100%': { opacity: 1 },
                                },
                            }}
                        />
                        <Chip size="sm" color="primary" sx={{ fontSize: '0.7rem', height: '20px', minHeight: '20px' }}>
                            AI Call {activeAvailableCall.call?.status === CallStatus.INITIATING ? '(Connecting...)' : ''}
                        </Chip>

                        {/* Call duration with activity indicator */}
                        <CallDurationTimer startedAt={activeAvailableCall.call?.startedAt} />

                        {/* Dispatcher request alert chip */}
                        {dispatcherRequestAlert && (
                            <Chip
                                size="sm"
                                color="warning"
                                variant="soft"
                                sx={{
                                    fontSize: '0.6rem',
                                    height: '18px',
                                    minHeight: '18px',
                                    animation: 'chipPulse 1s ease-in-out infinite alternate',
                                    '@keyframes chipPulse': {
                                        '0%': { transform: 'scale(1)' },
                                        '100%': { transform: 'scale(1.05)' },
                                    },
                                }}
                            >
                                Dispatcher Requested
                            </Chip>
                        )}

                        {/* Show takeover status for AI calls */}
                        {takenOver && (
                            <Chip size="sm" color="success" variant="soft" sx={{ fontSize: '0.6rem', height: '18px', minHeight: '18px' }}>
                                Taken Over
                            </Chip>
                        )}

                        {isListening && (
                            <Chip size="sm" color="primary" variant="soft" sx={{ fontSize: '0.6rem', height: '18px', minHeight: '18px' }}>
                                Listening
                            </Chip>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                            level="body-sm"
                            sx={{
                                ml: 'auto',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '150px',
                            }}
                        >
                            {activeAvailableCall.load.posterInfo.contact.phoneNumber}
                        </Typography>
                        {/* Transcript toggle button - only show for AI calls with transcripts */}
                        {(transcriptions?.length || 0) > 0 && (
                            <IconButton
                                size="sm"
                                variant="plain"
                                onClick={onExpandTranscriptions}
                                sx={{
                                    transition: 'transform 0.2s',
                                    transform: openTranscripts ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}
                            >
                                <ExpandMore fontSize="small" />
                            </IconButton>
                        )}

                        <IconButton
                            size="sm"
                            variant="plain"
                            onClick={onMaximize}
                            sx={{
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'scale(1.1)' },
                            }}
                        >
                            <OpenInFull fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: dispatcherRequestAlert ? 'warning.50' : rateHighlight ? 'success.50' : 'background.level1', // Default for everything else
                        p: 1,
                        borderRadius: 'sm',
                        overflow: 'hidden',
                        flexWrap: 'nowrap',
                        minHeight: '36px',
                        border: '1px solid',
                        borderColor: dispatcherRequestAlert ? 'warning.200' : rateHighlight ? 'success.200' : 'transparent', // Default for everything else
                    }}
                >
                    {/* Rate Information - Compact */}
                    {activeAvailableCall.load.rateInfo?.rateUsd ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            <Chip
                                size="sm"
                                color={rateHighlight ? 'success' : activeAvailableCall.load.rateInfo.isAIIdentified ? 'success' : 'primary'}
                                variant="soft"
                                sx={{
                                    fontSize: '1rem',
                                    height: '20px',
                                    minHeight: '20px',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.3,
                                    px: 0.8,
                                    py: 0.5,
                                    ...(rateHighlight &&
                                        !dispatcherRequestAlert && {
                                            animation: 'chipGlow 2s ease-in-out infinite alternate',
                                            '@keyframes chipGlow': {
                                                '0%': { transform: 'scale(1)' },
                                                '100%': { transform: 'scale(1.03)' },
                                            },
                                        }),
                                }}
                            >
                                {formatCurrency(activeAvailableCall.load.rateInfo.rateUsd)}
                                {activeAvailableCall.load.rateInfo.isAIIdentified && <AutoAwesome sx={{ fontSize: '1rem' }} />}
                            </Chip>
                            {/* Rate per mile - inline */}
                            {activeAvailableCall.load.tripLength && (
                                <Typography
                                    level="body-xs"
                                    sx={{
                                        color: 'primary',
                                        fontSize: '0.8rem',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    ({formatRatePerMile(activeAvailableCall.load.rateInfo.rateUsd, activeAvailableCall.load.tripLength)})
                                </Typography>
                            )}
                        </Box>
                    ) : null}
                    {/* Spacer */}
                    <Box sx={{ width: '1px', height: '20px', bgcolor: 'divider', flexShrink: 0 }} />

                    {/* Spacer */}
                    <Box sx={{ flex: 1 }} />

                    {/* Route - Compact */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <Route fontSize="small" color="primary" />
                        <Typography level="body-sm" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            {formatLoadLocation(activeAvailableCall.load.origin)}
                        </Typography>
                        <HorizontalDirectionIcon />
                        <Typography level="body-sm" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            {formatLoadLocation(activeAvailableCall.load.destination)}
                        </Typography>
                    </Box>

                    {/* Separator */}
                    <Box sx={{ width: '1px', height: '20px', bgcolor: 'divider', flexShrink: 0 }} />

                    {/* Trip Details - Compact */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        {activeAvailableCall.load.tripLength && (
                            <Chip
                                size="sm"
                                variant="soft"
                                color="primary"
                                sx={{
                                    fontSize: '0.8rem',
                                    height: '18px',
                                    minHeight: '18px',
                                    fontWeight: 'medium',
                                    px: 1,
                                }}
                            >
                                {activeAvailableCall.load.tripLength}mi
                            </Chip>
                        )}
                        {deadheadMiles > 0 && (
                            <Chip
                                size="sm"
                                variant="soft"
                                color="warning"
                                sx={{
                                    fontSize: '0.7rem',
                                    height: '16px',
                                    minHeight: '16px',
                                    px: 1,
                                }}
                            >
                                +{deadheadMiles}DH
                            </Chip>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        maxHeight: openTranscripts ? '200px' : '0px',
                        overflow: 'hidden',
                        transition: 'max-height 0.3s ease-in-out',
                    }}
                >
                    <Card
                        variant="outlined"
                        sx={{
                            mt: 1,
                            p: openTranscripts ? 1 : 0,
                            maxHeight: '200px',
                            overflow: 'auto',
                            bgcolor: dispatcherRequestAlert ? 'warning.50' : rateHighlight ? 'success.50' : 'background.level1', // Default for everything else
                            opacity: openTranscripts ? 1 : 0,
                            transition: 'opacity 0.2s ease-in-out, padding 0.3s ease-in-out',
                            border: '1px solid',
                            borderColor: dispatcherRequestAlert ? 'warning.200' : rateHighlight ? 'success.200' : 'divider', // Default for everything else
                            '&::-webkit-scrollbar': {
                                width: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'neutral.300',
                                borderRadius: '2px',
                            },
                        }}
                    >
                        {openTranscripts && (transcriptions?.length || 0) > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                    <Chat fontSize="small" color="primary" />
                                    <Typography level="body-xs" sx={{ fontWeight: 'medium', color: 'primary.600' }}>
                                        Live Transcript
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 1,
                                                width: 6,
                                                height: 6,
                                                borderRadius: '50%',
                                                bgcolor: 'success.500',
                                                display: 'inline-block',
                                                animation: 'pulse 2s infinite',
                                                '@keyframes pulse': {
                                                    '0%': { opacity: 1 },
                                                    '50%': { opacity: 0.5 },
                                                    '100%': { opacity: 1 },
                                                },
                                            }}
                                        />
                                    </Typography>

                                    {/* Rate indicator in transcript header */}
                                    {activeAvailableCall.load.rateInfo?.rateUsd && (
                                        <Typography
                                            level="body-xs"
                                            sx={{
                                                fontWeight: 'bold',
                                                color: rateHighlight ? 'success.600' : 'success.400',
                                                ...(rateHighlight &&
                                                    !dispatcherRequestAlert && {
                                                        animation: 'textGlow 2s ease-in-out infinite alternate',
                                                        '@keyframes textGlow': {
                                                            '0%': { opacity: 0.8 },
                                                            '100%': { opacity: 1 },
                                                        },
                                                    }),
                                            }}
                                        >
                                            Rate: {formatCurrency(activeAvailableCall.load.rateInfo.rateUsd)}
                                        </Typography>
                                    )}

                                    {/* Dispatcher request indicator in transcript header */}
                                    {dispatcherRequestAlert && (
                                        <Typography
                                            level="body-xs"
                                            sx={{
                                                fontWeight: 'bold',
                                                color: 'warning.600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                animation: 'urgentPulse 1.5s ease-in-out infinite alternate',
                                                '@keyframes urgentPulse': {
                                                    '0%': { opacity: 0.7 },
                                                    '100%': { opacity: 1 },
                                                },
                                            }}
                                        >
                                            <NotificationImportant sx={{ fontSize: '0.9rem' }} />
                                            Human Dispatcher Needed!
                                        </Typography>
                                    )}
                                </Box>

                                {transcriptions?.slice(-3)?.map((transcript, index) => {
                                    // Check if this transcript is a tool transcript with "Set Rate"
                                    const content = Array.isArray(transcript.content) ? transcript.content.join(' ') : transcript.content
                                    const isSetRateMessage = transcript.role === VoiceTranscriptRole.TOOL && content.toLowerCase().includes('set rate')
                                    const isDispatcherRequestMessage =
                                        transcript.role === VoiceTranscriptRole.TOOL && content.toLowerCase().includes('request human dispatcher')
                                    return (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: transcript.role === VoiceTranscriptRole.ASSISTANT ? 'flex-start' : 'flex-end',
                                                mb: 0.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    maxWidth: transcript.role === VoiceTranscriptRole.TOOL ? '95%' : '85%',
                                                    p: 1,
                                                    borderRadius: transcript.role === VoiceTranscriptRole.TOOL ? 'sm' : 'lg',
                                                    // Only color specific message types
                                                    bgcolor:
                                                        transcript.role === VoiceTranscriptRole.TOOL && isDispatcherRequestMessage
                                                            ? 'warning.100' // Pale orange for dispatcher requests only
                                                            : transcript.role === VoiceTranscriptRole.TOOL && isSetRateMessage
                                                              ? 'success.100' // Pale green for rate messages only
                                                              : transcript.role === VoiceTranscriptRole.ASSISTANT
                                                                ? 'primary.50' // Keep assistant messages colored
                                                                : 'background.surface', // Default for everything else
                                                    border: '1px solid',
                                                    borderColor:
                                                        transcript.role === VoiceTranscriptRole.TOOL && isDispatcherRequestMessage
                                                            ? 'warning.400' // Border for dispatcher requests only
                                                            : transcript.role === VoiceTranscriptRole.TOOL && isSetRateMessage
                                                              ? 'success.400' // Border for rate messages only
                                                              : transcript.role === VoiceTranscriptRole.ASSISTANT
                                                                ? 'primary.200' // Keep assistant borders
                                                                : 'divider', // Default border for everything else

                                                    // Enhanced styling for dispatcher request messages
                                                    ...(isDispatcherRequestMessage && {
                                                        bgcolor: 'warning.100',
                                                        borderColor: 'warning.400',
                                                        borderWidth: '2px',
                                                        animation: 'dispatcherMessagePulse 2s ease-in-out infinite alternate',
                                                        '@keyframes dispatcherMessagePulse': {
                                                            '0%': {
                                                                bgcolor: 'warning.100',
                                                                transform: 'scale(1)',
                                                            },
                                                            '100%': {
                                                                bgcolor: 'warning.200',
                                                                transform: 'scale(1.01)',
                                                            },
                                                        },
                                                    }),

                                                    // Enhanced styling for "Set rate:" messages (only if no dispatcher alert)
                                                    ...(isSetRateMessage &&
                                                        rateHighlight &&
                                                        !dispatcherRequestAlert && {
                                                            bgcolor: 'success.100',
                                                            borderColor: 'success.400',
                                                            borderWidth: '2px',
                                                            animation: 'setRateMessagePulse 2s ease-in-out infinite alternate',
                                                            '@keyframes setRateMessagePulse': {
                                                                '0%': {
                                                                    bgcolor: 'success.100',
                                                                    transform: 'scale(1)',
                                                                },
                                                                '100%': {
                                                                    bgcolor: 'success.200',
                                                                    transform: 'scale(1.01)',
                                                                },
                                                            },
                                                        }),
                                                    ...(transcript.role === VoiceTranscriptRole.TOOL && {
                                                        borderStyle: 'dashed',
                                                    }),
                                                }}
                                            >
                                                <Typography
                                                    level="body-xs"
                                                    sx={{
                                                        color:
                                                            transcript.role === VoiceTranscriptRole.ASSISTANT
                                                                ? 'primary.700'
                                                                : transcript.role === VoiceTranscriptRole.TOOL
                                                                  ? isDispatcherRequestMessage
                                                                      ? 'warning.800'
                                                                      : isSetRateMessage
                                                                        ? 'success.700'
                                                                        : 'warning.700'
                                                                  : 'text.primary',
                                                        fontWeight:
                                                            transcript.role === VoiceTranscriptRole.ASSISTANT
                                                                ? 'medium'
                                                                : transcript.role === VoiceTranscriptRole.TOOL
                                                                  ? 'bold'
                                                                  : 'normal',
                                                        lineHeight: 1.3,
                                                        fontStyle: transcript.role === VoiceTranscriptRole.TOOL ? 'italic' : 'normal',

                                                        // Extra emphasis for dispatcher request messages
                                                        ...(isDispatcherRequestMessage && {
                                                            fontWeight: 'bold',
                                                            color: 'warning.900',
                                                        }),

                                                        // Extra emphasis for "Set rate:" messages (only if no dispatcher alert)
                                                        ...(isSetRateMessage &&
                                                            rateHighlight &&
                                                            !dispatcherRequestAlert && {
                                                                fontWeight: 'bold',
                                                                color: 'success.800',
                                                            }),
                                                    }}
                                                >
                                                    <Box component="span" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                                                        {
                                                            {
                                                                [VoiceTranscriptRole.ASSISTANT]: '🤖',
                                                                [VoiceTranscriptRole.TOOL]: isDispatcherRequestMessage ? '🚨' : isSetRateMessage ? '💰' : '🔧',
                                                                [VoiceTranscriptRole.USER]: '👤',
                                                                [VoiceTranscriptRole.DISPATCHER]: '🎧',
                                                            }[transcript.role]
                                                        }
                                                    </Box>{' '}
                                                    {transcript.tool ? convertVoiceAgentToolCallToMessage(transcript.tool) : transcript.content}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )
                                })}

                                {(transcriptions?.length || 0) > 3 && (
                                    <Typography
                                        level="body-xs"
                                        sx={{
                                            textAlign: 'center',
                                            color: 'text.secondary',
                                            fontStyle: 'italic',
                                            mt: 0.5,
                                        }}
                                    >
                                        +{(transcriptions?.length || 0) - 3} more transcripts
                                    </Typography>
                                )}

                                <div ref={transcriptsDivRef} />
                            </Box>
                        ) : openTranscripts ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '60px',
                                    flexDirection: 'column',
                                    gap: 1,
                                }}
                            >
                                <Typography level="body-xs" color="neutral">
                                    Waiting for conversation...
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {[0, 1, 2].map((i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                width: 3,
                                                height: 3,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.300',
                                                animation: `bounce 1.4s infinite ease-in-out both`,
                                                animationDelay: `${i * 0.16}s`,
                                                '@keyframes bounce': {
                                                    '0%, 80%, 100%': {
                                                        transform: 'scale(0)',
                                                    },
                                                    '40%': {
                                                        transform: 'scale(1)',
                                                    },
                                                },
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        ) : null}
                    </Card>
                </Box>

                {/* Control buttons */}
                <Box sx={{ display: 'flex', gap: 1, pt: 0 }}>
                    {/* Conditional button rendering based on call type */}
                    {!takenOver && (
                        <Button
                            size="sm"
                            color={isListening ? 'warning' : 'primary'}
                            variant="soft"
                            startDecorator={isListening ? <HeadsetOff fontSize="small" /> : <Headset fontSize="small" />}
                            onClick={onToggleListeningMode}
                            disabled={takenOver || loadingTakeOver}
                            sx={{
                                flex: 1,
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                },
                                ...(isListening && {
                                    animation: 'listenPulse 2s infinite',
                                    '@keyframes listenPulse': {
                                        '0%, 100%': {
                                            bgcolor: 'warning.50',
                                        },
                                        '50%': {
                                            bgcolor: 'warning.100',
                                        },
                                    },
                                }),
                            }}
                        >
                            {isListening ? 'Listening' : 'Listen'}
                        </Button>
                    )}

                    {/* Take Over Button - show when not listening and not taken over */}
                    {takenOver ? (
                        <Button
                            size="sm"
                            color="success"
                            variant="soft"
                            startDecorator={<CheckCircle fontSize="small" />}
                            disabled={true}
                            sx={{
                                flex: 1,
                                cursor: 'default',
                                '&.Mui-disabled': {
                                    opacity: 1,
                                },
                            }}
                        >
                            Taken Over
                        </Button>
                    ) : (
                        <Button
                            size="sm"
                            color={dispatcherRequestAlert ? 'warning' : 'primary'}
                            variant={dispatcherRequestAlert ? 'solid' : 'soft'}
                            startDecorator={dispatcherRequestAlert ? <NotificationImportant fontSize="small" /> : <Person fontSize="small" />}
                            onClick={onTakeOverButtonClick}
                            disabled={loadingTakeOver}
                            loading={loadingTakeOver}
                            sx={{
                                flex: 1,
                                transition: 'all 0.2s',
                                '&:hover': { transform: 'translateY(-2px)' },
                                // Enhanced styling for dispatcher request
                                ...(dispatcherRequestAlert && {
                                    fontWeight: 'bold',
                                    animation: 'takeOverUrgent 1.5s ease-in-out infinite alternate',
                                    '@keyframes takeOverUrgent': {
                                        '0%': {
                                            transform: 'scale(1)',
                                            bgcolor: 'warning.500',
                                        },
                                        '100%': {
                                            transform: 'scale(1.03)',
                                            bgcolor: 'warning.600',
                                        },
                                    },
                                }),
                            }}
                        >
                            {loadingTakeOver ? 'Taking...' : dispatcherRequestAlert ? 'Take Over NOW!' : 'Take Over'}
                        </Button>
                    )}

                    {/* End Call button - same for both types */}
                    <Button
                        size="sm"
                        color="danger"
                        variant="soft"
                        startDecorator={<CallEnd fontSize="small" />}
                        onClick={onEndCallButtonClick}
                        loading={endingCall}
                        sx={{
                            flex: 1,
                            transition: 'all 0.2s',
                            '&:hover': { transform: 'translateY(-2px)' },
                        }}
                    >
                        End
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
